import { cloneDeep } from 'lodash';
import {
  AccountClient,
  apiResolver,
  GET_CUSTOM_FIELD_BY_NAME,
  GET_REQUESTERS_BY_CRITERIA,
  GET_CLIENTS,
  GET_REQUESTERS_BY_CRITERIA_ASSOCIATIONS,
  RMMClient
} from '@nile/graphql';
import { GET_CLIENT_SITE_LIST_FOR_DROPDOWN } from '@nile/banyan/src/Graphql/Mutations/site';
import { ALL_MODULES } from '@nile/components';
import { isEmpty } from 'lodash';
import { assetClassName } from '@nile/rmm/src/modules/asset-management/components/constants/asset-rule-builder';
import { GET_ASSETS_FILTER } from '@nile/rmm/src/modules/asset-management/Graphql/asset';
import { ALL_ASSET_TYPES } from '@nile/rmm/src/constants';

let sourceConfig = {};

const setConfig = (config) => {
  sourceConfig = config;
};

const getConfig = () => {
  return sourceConfig;
};

const getDefaultValue = (f, condition) => {
  const operands = cloneDeep(condition.operands);
  const ruleIdx = operands.findIndex((el) => {
    if (f.dataType === 'entity') {
      return el.attribute === `${f.name}.name`;
    }
    return el.attribute === f.name;
  });
  if (ruleIdx > -1) {
    const rule = operands[ruleIdx];
    switch (f.dataType) {
      case 'date':
      case 'datetime':
        return rule.value;
      default:
        break;
    }
    switch (f.filterType) {
      case 'text':
      case 'numeric':
      case 'decimal':
      case 'paragraph':
      case 'read_only':
        return rule.value;
      default:
        return rule.value.map((el) => {
          return {
            name: el,
            value: el,
            label: el
          };
        });
    }
  }
  return '';
};

/**
 * @description This method gets triggered for custom field or any other field for which we want the query to be dynamic (refer checkDynamicQuery function)
 * Calls the respective API based on isCustom/attribute name
 * @param {object} - f
 * @return {object} - query
 */
const getDynamicQuery = (f) => {
  const resolverModule = 'Asset';
  let client = RMMClient;
  let query = apiResolver[resolverModule][f.name]?.api;

  switch (f.name) {
    case 'client.name':
      client = AccountClient;
      query = GET_CLIENTS;
      return client.query({
        query: query,
        variables: {
          listInfo: {}
        }
      });
    case 'site.name':
      // We need to filter sites by client name by default in client details -> assets (this condition will only be applied in client details -> assets)
      let siteCondition = getConfig()?.site;
      return AccountClient.query({
        query: GET_CLIENT_SITE_LIST_FOR_DROPDOWN,
        variables: !isEmpty(siteCondition)
          ? {
              // Deprecation special handling: TODO: Special
              input: {
                condition: {
                  joinOperator: 'AND',
                  operands: [siteCondition]
                },
                sort: {
                  attribute: 'name',
                  order: 'ASC'
                }
              }
            }
          : {
              input: {
                sort: {
                  attribute: 'name',
                  order: 'ASC'
                }
              }
            }
      });
    case 'status':
      return {
        data: [
          {
            label: 'ONLINE',
            value: 'ONLINE'
          },
          {
            label: 'OFFLINE',
            value: 'OFFLINE'
          },
          {
            label: 'UNKNOWN',
            value: 'UNKNOWN'
          }
        ]
      };
    case 'monitoringStatus':
      return {
        data: [
          {
            label: 'Supported',
            name: 'SUPPORTED',
            value: 'SUPPORTED'
          },
          {
            label: 'Not Supported',
            name: 'NOT_SUPPORTED',
            value: 'NOT_SUPPORTED'
          }
        ]
      };
    case 'name':
      client = RMMClient;
      query = GET_ASSETS_FILTER;
      return client.query({
        query: query,
        variables: {
          listInfo: {
            view: {
              id: 7001
            }
          },
          viewType: ALL_ASSET_TYPES.ALL_MONITORED_ASSET //TODO: Fix
        }
      });
    case 'assetClass.name':
      query = apiResolver[resolverModule][f.name]?.api;
      client = apiResolver[resolverModule][f.name]?.client;
      return client.query({
        query: query,
        variables: {
          moduleType: ['UN_MONITORED_ASSET', 'ASSET']
        }
      });
    default:
      if (f.isCustom) {
        return client.query({
          query: GET_CUSTOM_FIELD_BY_NAME,
          variables: {
            columnName: f.name,
            module: ALL_MODULES.asset,
            assetClassEntityName: 'WorkStation'
          }
        });
      } else {
        query = apiResolver[resolverModule][f.name]?.api;
        client = apiResolver[resolverModule][f.name]?.client;
        return client.query({
          query: query
        });
      }
  }
};

/**
 * @description This method check whether a particular attribute requires the query to be dynamic or not
 * @param {object} - f
 * @return {boolean}
 * @public
 */
const checkDynamicQuery = (f) => {
  if (f.isCustom) return true;
  switch (f.name) {
    case 'name':
    case 'site.name':
    case 'client.name':
    case 'status':
    case 'assetClass.name':
    case 'monitoringStatus':
      return true;
    default:
      return false;
  }
};

/**
 * @description This method is responsible to format the data according to the react-select for the dynamic query attributes
 * @param {object} - f
 * @param {Array<object>} - data
 * @return {Array | Array<object>}
 * @public
 */
const generateOptions = (f, data) => {
  let allValues = [];
  switch (f.name) {
    case 'client.name':
      allValues = data.getClientList.clients.map((el) => {
        return {
          ...el,
          name: el.accountId,
          value: el.name,
          label: el.name
        };
      });
      break;
    case 'site.name':
      allValues = data.getClientSiteList.sites.map((el) => {
        return {
          ...el,
          name: el.accountId,
          value: el.name,
          label: el.name
        };
      });
      break;
    case 'name':
      allValues = data.getAllAssets.assets.map((el) => {
        return {
          name: el.assetId,
          value: el.name,
          label: el.name
        };
      });
      break;
    case 'assetClass.name':
      allValues = data.getAssetClassListV2?.map((el) => {
        return {
          name: el.name,
          value: el.name,
          label: el.name
        };
      });
      return allValues;
    case 'status':
    case 'monitoringStatus':
      allValues = allValues.concat(data);
      break;
    default:
      if (f.isCustom) {
        allValues = data.getCustomFieldByName.options.map((el) => {
          return {
            name: el,
            value: el,
            label: el
          };
        });
        return allValues;
      } else {
        allValues = [];
      }
  }
  return allValues;
};

/**
 * @description There are few attributes for which we don't want to fetch the values from the server on selecting the attribute
 * rather, we want to fetch on typing atleast 2 character, that is when this function gets triggered (in the attribute object, we have 'autocomplete' property as true)
 * @param {object} - client
 * @param {object} - query
 * @param {object} - params
 * @return {Array | Array<object>}
 * @public
 */
const fetchValues = async (client, query, params) => {
  const attribute = 'name';
  const nameKey = getNameKey(params.property);

  let variables = {
    listInfo: {
      condition: {
        joinOperator: 'AND',
        operands: [
          {
            attribute: attribute,
            operator: 'contains',
            value: params.inputValue
          }
        ]
      }
    }
  };

  const isNameColumnSearch =
    params?.property?.name === 'name' && nameKey === 'getAllAssets';

  if (isNameColumnSearch) {
    variables = {
      ...variables,
      viewType: 'ALL_ASSET'
    };
  }

  // We need to filter assets by client name by default in client details -> assets (this condition will only be applied in client details -> assets)
  let assetCondition = getConfig()?.asset;
  if (!isEmpty(assetCondition) && nameKey === 'getWorkStationList') {
    variables.listInfo.condition.operands.push(assetCondition);
  }

  const res = await client.query({
    query: query,
    variables
  });

  let allOptions = [];

  if (params?.property?.name === 'owner.name') {
    allOptions = res?.data?.[nameKey]
      ? res.data[nameKey]?.userList?.map((el) => {
          return {
            name: `${el[attribute]}`,
            value: `${el[attribute]}`,
            label: `${el[attribute]}`
          };
        })
      : [];
  } else if (isNameColumnSearch) {
    allOptions = res?.data?.[nameKey]
      ? res.data[nameKey]?.assets?.map((el) => {
          return {
            name: `${el[attribute]}`,
            value: `${el[attribute]}`,
            label: `${el[attribute]}`
          };
        })
      : [];
  } else {
    allOptions = res?.data?.[nameKey]
      ? res.data[nameKey].map((el) => {
          return {
            name: `${el[attribute]}`,
            value: `${el[attribute]}`,
            label: `${el[attribute]}`
          };
        })
      : [];
  }

  return allOptions;
};

/**
 * @description returns the graphql schema based on the attribute
 * @param {object} - property
 * @return {object}
 * @private
 */
function getQuery(item) {
  switch (item.name) {
    case 'name':
      return GET_ASSETS_FILTER;
    case 'owner.name':
      return GET_REQUESTERS_BY_CRITERIA;
    default:
      break;
  }
}

/**
 * @description returns the accessKey based on the attribute
 * @param {object} - property
 * @return {string}
 * @private
 */
function getNameKey(property) {
  switch (property.name) {
    case 'name':
      return 'getAllAssets';
    default:
      return 'getRequesterList';
  }
}

/**
 * @description returns the accessKey based on the attribute
 * @param {object} - property
 * @return {string}
 * @private
 */
function getQueryClient(property) {
  switch (property.name) {
    case 'owner.name':
      return AccountClient;
    default:
      return RMMClient;
  }
}

/**
 * @description returns RMMClient and query based on the selected attribute
 * @param {object} - property
 * @return {object}
 * @public
 */
const getAsyncOptions = (property) => {
  switch (property.name) {
    case 'owner.name':
      return {
        client: AccountClient,
        query: GET_REQUESTERS_BY_CRITERIA_ASSOCIATIONS
      };
    default:
      return { client: getQueryClient(property), query: getQuery(property) };
  }
};

const getVariables = (property) => {
  let baseVariables = {};
  if (!isEmpty(getConfig())) {
    baseVariables = getConfig();
  }
  switch (property.name) {
    case 'site.name':
      return { listInfo: { ...baseVariables } };
    default:
      return null;
  }
};

const getAttributeName = (column: { name: string }) => {
  return column?.name;
};

export {
  getDefaultValue,
  getDynamicQuery,
  checkDynamicQuery,
  generateOptions,
  fetchValues,
  getAsyncOptions,
  getVariables,
  getAttributeName,
  setConfig,
  getConfig
};
